/**
 * Reinicia el caché de productos (listado + posición de scroll) del catálogo
 * cuando la página se recarga de verdad (F5, Ctrl+R, escribir la URL de
 * nuevo, etc.), pero lo deja intacto durante la navegación interna de la SPA
 * (por ejemplo, al entrar al detalle de un producto y volver con el botón
 * "volver"), para que la restauración de scroll/paginación siga funcionando.
 *
 * Debe llamarse una sola vez, antes de montar la app (ver src/main.jsx),
 * porque `performance.getEntriesByType("navigation")` solo refleja la carga
 * real del documento y no cambia con la navegación de React Router.
 */
import { STORAGE_KEYS } from "../hooks/useNavigationMemory";

const isRealPageReload = () => {
  try {
    const [navEntry] = performance.getEntriesByType("navigation");
    if (navEntry) {
      return navEntry.type === "reload";
    }
  } catch {
    // Ignorar y usar el fallback de abajo
  }

  // Fallback para navegadores sin soporte de Navigation Timing L2
  return (
    typeof performance !== "undefined" &&
    performance.navigation &&
    performance.navigation.type === performance.navigation.TYPE_RELOAD
  );
};

export const resetProductsCacheOnReload = () => {
  // 🚫 Evitar que el navegador restaure el scroll por su cuenta (comportamiento
  // nativo del navegador en recargas/back-forward). Así el scroll queda
  // controlado únicamente por nuestra lógica de navegación con memoria.
  try {
    if (typeof history !== "undefined" && "scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
  } catch (err) {
    console.warn("[resetProductsCacheOnReload] No se pudo desactivar scrollRestoration:", err);
  }

  if (!isRealPageReload()) return;

  try {
    sessionStorage.removeItem(STORAGE_KEYS.PRODUCTS_STATE);
    sessionStorage.removeItem(STORAGE_KEYS.SCROLL_POSITION);
    sessionStorage.removeItem(STORAGE_KEYS.FILTERS_STATE);

    // Forzar el scroll al tope, ya que la recarga no debe conservar la
    // posición previa (ni la del navegador ni la guardada por la app).
    window.scrollTo(0, 0);

    console.info(
      "[resetProductsCacheOnReload] Página recargada: caché de productos y scroll reiniciados"
    );
  } catch (err) {
    console.warn("[resetProductsCacheOnReload] No se pudo limpiar:", err);
  }
};
